/**
 * Core State Management
 * 
 * Centralized state container with Immer-based immutability.
 * Automatically emits events on state changes and records history snapshots.
 * Listens for restoration events (undo/redo/hydration) to rehydrate state.
 */

import { produce } from 'immer';
import { bus } from './events.js';
import { history } from './history.js';
import { throttle } from '../utils/throttle.js';
import { log } from '../services/logging/index.js';
import type { User, Decision, DecisionCard } from '../types/interfaces.js';
import type { GridState } from '../types/square.js';
import type { GridElement } from '../grid-allocation-v2/types.js';
import type { AnimationProfileType } from '../types/enums.js';

/**
 * Canonical state shape for Pivot.is application.
 * 
 * This interface represents the global application state.
 * Extend this as new domains are added to the application.
 */
export interface PivotState {
  user?: User;
  layout?: {
    profile?: string | null;
    animationProfile?: AnimationProfileType;
    geometry?: unknown;
  };
  grid?: GridState;
  elements?: GridElement[];
  decisions?: Record<string, Decision>;
  cards?: Record<string, DecisionCard>;
  presence?: Record<string, unknown>;
  ui?: { 
    selection?: string | null; 
    fps?: number; 
    debug?: boolean; // Deprecated: use gridVisualization instead
    gridVisualization?: 'none' | 'debug' | 'verification' | 'both' | 'avoidance'; // Grid visualization mode (persisted)
    backgroundIndex?: number;
    backgroundType?: string; // Background type (e.g., 'image', 'gradient', 'majaku-privacy') - avoids brittle index checking
    backgroundData?: { gradient?: { topColor: string; bottomColor: string } }; // Background-specific data
    idleCameraMotion?: 'circular-orbit' | 'pendulum-orbit' | 'lissajous' | 'helix' | 'golden-ratio-orbit' | 'micro-dolly-breathing' | 'debug-rotation' | 'organic-jostle'; // Idle camera motion mode (persisted)
    activeNetworkRequests?: number; // Number of currently active network requests (fetch, XMLHttpRequest)
    metaball?: {
      useOverlay?: boolean; // true = overlay metaball, false = original metaball (default: false for better quality)
      originalOpacity?: number; // Opacity of original metaball (0-1, default: 1)
      overlayOpacity?: number; // Opacity of overlay metaball (0-1, default: 0)
    };
  };
  microphone?: { isListening: boolean; isAvailable: boolean; errorMessage?: string };
  windowFocus?: {
    isFocused: boolean;
    allowBackgroundConversation: boolean | null; // null = not asked yet, true/false = user preference
    lastFocusCheckDate: string; // ISO date string for throttling (once per day)
    lastAskedDate: string; // ISO date string for when we last asked about background conversation
  };
  processes?: Record<string, {
    kind: string;
    id: string;
    persist?: boolean;
    // View persistence: grid allocation, position, and expanded state
    views?: Array<{
      id: string;
      gridAllocation?: {
        cols: number;  // actualCols (grid cells)
        rows: number;  // actualRows (grid cells)
      };
      gridPosition?: {
        x: number;  // grid column (top-left cell)
        y: number;  // grid row (top-left cell)
      };
      expanded?: boolean;  // expanded/contracted state
    }>;
  }>;
  // View order: simple array of view IDs representing left-to-right order (index 0 = leftmost)
  // Positions are inferred from order: full-height views flow horizontally, half-height views at same index stack vertically
  viewOrder?: string[];
  // Add more domains as needed
}

const _state: PivotState = {};

/**
 * Flag to suppress event emission during restore operations.
 * Prevents duplicate events when restoring state.
 */
let isRestoring = false;

/**
 * Flag to prevent infinite loops when handling remote state.update events.
 * When true, setState() won't emit events (preventing re-emission of remote updates).
 */
let isUpdatingFromRemote = false;

/**
 * Throttle history snapshots to avoid every micro-mutation creating a new entry.
 * Records history at most once per 200ms.
 */
const recordThrottled = throttle(() => history.record(_state as Record<string, unknown>), 200);

/**
 * Update state using Immer and emit events automatically.
 * 
 * This is the primary way to update state. It:
 * 1. Uses Immer for immutable updates
 * 2. Emits fine-grained event: `state.${key}`
 * 3. Emits coarse-grained event: `state.update`
 * 4. Records throttled history snapshot
 * 5. Persists to localStorage (via transport)
 * 
 * Example:
 * ```typescript
 * updateState('layout', draft => {
 *   draft.profile = 'kanban';
 * });
 * ```
 */
export function updateState<K extends keyof PivotState>(
  key: K,
  updater: (draft: NonNullable<PivotState[K]>) => void
): void {
  const currentValue = _state[key];

  if (currentValue === undefined || currentValue === null) {
    // If current value is undefined, we can't update it - use setState instead
    throw new Error(`Cannot update undefined state.${String(key)}. Use setState() instead.`);
  }

  // Use Immer to produce new immutable value
  // Type assertion: we know currentValue is non-null at this point
  const nextValue = produce(currentValue as NonNullable<PivotState[K]>, updater) as PivotState[K];

  // Assign to internal state
  _state[key] = nextValue;

  // Skip internal animation properties (px, target, vel, velOpacity)
  // These change every frame and shouldn't trigger events
  if (key === 'squares' && Array.isArray(nextValue)) {
    // Check if this is just an animation update
    // We can't easily detect this, but we can skip if squares array reference is unchanged
    // Actually, with Immer, the reference always changes if any property changed
    // So we need a different approach - check if only animation properties changed
    // For now, we'll emit for all squares updates (caller should batch if needed)
  }

  // Log state change
  let serializedValue: unknown = nextValue;
  if (nextValue !== null && typeof nextValue === 'object' && !(nextValue instanceof Date)) {
    try {
      serializedValue = JSON.parse(JSON.stringify(nextValue));
    } catch {
      serializedValue = `[${typeof nextValue}]`;
    }
  }

  // Emit events (unless restoring or updating from remote)
  if (!isRestoring && !isUpdatingFromRemote) {
    (bus as any).emit(`state.${String(key)}`, nextValue);
    bus.emit('state.update', { key: String(key), value: nextValue });

    // Record history (throttled)
    recordThrottled();
  }
}


/**
 * Set state directly (for simple replacements).
 * 
 * Example:
 * ```typescript
 * setState('layout', { profile: 'kanban', presentationMode: false });
 * ```
 */
export function setState<K extends keyof PivotState>(
  key: K,
  value: PivotState[K]
): void {
  _state[key] = value;

  // Log state change
  let serializedValue: unknown = value;
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    try {
      serializedValue = JSON.parse(JSON.stringify(value));
    } catch {
      serializedValue = `[${typeof value}]`;
    }
  }

  // Emit events (unless restoring or updating from remote)
  if (!isRestoring && !isUpdatingFromRemote) {
    (bus as any).emit(`state.${String(key)}`, value);
    bus.emit('state.update', { key: String(key), value });

    // Record history (throttled)
    recordThrottled();
  }
}

/**
 * Read-only state accessor.
 * Use this to read state without triggering any reactivity.
 */
export const state = _state as PivotState;

/**
 * Register state event listeners.
 * This is called at module load and can be called again after clearing listeners (e.g., in tests).
 */
function registerStateListeners(): void {
  /**
   * Listen for remote state.update events from WebSocket/transports.
   * Converts them to setState() calls using Immer.
   * 
   * This handler only processes remote events (marked with source: 'remote').
   * Local setState() calls emit state.update events but don't have source marker,
   * so they're ignored here to prevent loops.
   */
  bus.on('state.update', (payload: unknown) => {
    // Check if payload has source marker (indicates remote event)
    if (typeof payload === 'object' && payload !== null && 'source' in payload) {
      const update = payload as { key: string; value: unknown; source?: string };

      // Only process remote events
      if (update.source === 'remote' || update.source === 'transport') {
        const key = update.key as keyof PivotState;
        const value = update.value;

        // Skip if state hasn't changed (avoid unnecessary updates)
        // Use deep equality check since objects may have same content but different reference
        if (_state[key] === value) {
          return;
        }
        // If both are objects, do a shallow equality check
        if (typeof _state[key] === 'object' && _state[key] !== null && typeof value === 'object' && value !== null) {
          // For now, allow update (objects with same content but different reference should update)
          // In future, could use deep equality here
        }

        // Update state using Immer (via setState)
        // Suppress events to avoid re-emitting (already emitted by transport)
        isUpdatingFromRemote = true;
        try {
          setState(key, value as PivotState[typeof key]);
        } finally {
          isUpdatingFromRemote = false;
        }
      }
    }
    // If no source marker, this is a local event - ignore (already handled by setState)
  });

  /**
   * Listen for microphone state changes and update centralized state.
   */
  bus.on('microphone.listening.changed', (payload: unknown) => {
    const data = payload as { isListening: boolean; timestamp: number };
    if (!_state.microphone) {
      setState('microphone', {
        isListening: data.isListening,
        isAvailable: true,
      });
    } else {
      updateState('microphone', (draft) => {
        draft.isListening = data.isListening;
      });
    }
  });

  /**
   * Listen for restores (undo/redo/hydration) and replace entire state safely.
   */
  bus.on('state.restore', (snapshot: Record<string, unknown>) => {
    // Temporarily pause recording to avoid recursive history pollution
    history.setEnabled(false);
    isRestoring = true;

    try {
      // Deep clone snapshot to break any references
      let clonedSnapshot: PivotState;
      try {
        if (typeof structuredClone !== 'undefined') {
          clonedSnapshot = structuredClone(snapshot) as PivotState;
        } else {
          clonedSnapshot = JSON.parse(JSON.stringify(snapshot)) as PivotState;
        }
      } catch (error) {
        clonedSnapshot = snapshot;
      }

      // Clear existing state - replace entire object to ensure all keys are removed
      const stateKeys = Object.keys(_state) as (keyof PivotState)[];
      for (const k of stateKeys) {
        delete (_state as Record<string, unknown>)[k];
      }

      // Restore from cloned snapshot
      const keys = Object.keys(clonedSnapshot) as (keyof PivotState)[];
      for (const k of keys) {
        (_state as Record<string, unknown>)[k] = clonedSnapshot[k];
      }

      // Emit events asynchronously to avoid loops during restore
      // Keep isRestoring = true until events are emitted
      Promise.resolve().then(() => {
        // Re-emit each domain so UI can update
        const emitKeys = Object.keys(_state) as (keyof PivotState)[];
        for (const k of emitKeys) {
          const v = _state[k];
          (bus as any).emit(`state.${String(k)}`, v);
        }

        // Emit global update signal
        bus.emit('state.update', { key: '*', value: _state });

        // Now safe to set isRestoring = false and re-enable history after events are emitted
        isRestoring = false;
        history.setEnabled(true);
      });
    } finally {
      // Don't set isRestoring = false here - it's set in Promise.then() after events emit
      // But do re-enable history defensively in case Promise fails
      // Promise will also re-enable it, but that's OK (idempotent)
      if (isRestoring) {
        // Only set here if Promise hasn't run yet (defensive)
        // Promise will handle it, but this ensures it happens even if Promise fails
        history.setEnabled(true);
      }
    }
  });
}

// Register listeners at module load
registerStateListeners();

/**
 * Get the underlying state object (bypassing proxy) for safe operations like spreading.
 * Use this when you need to iterate or spread state without triggering proxy traps.
 */
export function _getInternalState(): PivotState {
  return _state;
}

/**
 * Re-register state listeners after clearing event bus (e.g., in tests).
 * Exported for test setup.
 */
export function _registerStateListeners(): void {
  registerStateListeners();
}
